/**
 * <FlagProgressBar> — a progress bar with a planted flag milestone (atom, L0.1).
 *
 * The live-report chapter-mastery bar: a rounded track with a `value` fill (achieved), an optional
 * lighter `flagAt` "pending" fill for the assigned-but-remaining stretch, and a flag + vertical marker
 * line planted at the `flagAt` milestone. Mirrors the legacy `ChapterItemComponent` XP bar (green fill,
 * grey remaining, flag that turns from accent to success once the assigned work is done). Composes the
 * `<Icon>` atom for the flag glyph. No hex: every color is a `theme.colors.*` token.
 */
import React from "react"
import { View } from "react-native"
import { StyleSheet } from "react-native-unistyles"

import { type SemanticColorKey } from "../../theme/tokens/colors"
import { Icon } from "../icons/Icon"

export type FlagProgressBarProps = {
    /** Primary (achieved) fill percentage, 0–100 (clamped). */
    value: number
    /** Flag milestone position, 0–100 — defaults to `value`. The [value, flagAt] gap renders as the
     *  lighter `pendingColor` fill (assigned work not yet done). */
    flagAt?: number
    /** Achieved-fill color token — defaults to `success`. */
    color?: SemanticColorKey
    /** Flag + marker-line color — defaults to `color`. */
    flagColor?: SemanticColorKey
    /** Pending-region ([value, flagAt]) fill color — defaults to `border`. */
    pendingColor?: SemanticColorKey
    /** Track (background) color token — defaults to `surface-subtle`. */
    trackColor?: SemanticColorKey
    /** Bar thickness in px — defaults to 12. */
    height?: number
    /** Optional content shown above the bar, aligned to its start (e.g. a "75 / 100" score). */
    label?: React.ReactNode
    testID?: string
}

const FLAG_SIZE = 16

const clamp = (n: number): number => Math.max(0, Math.min(100, n))

export function FlagProgressBar(props: FlagProgressBarProps) {
    const {
        value,
        flagAt,
        color = "success",
        flagColor,
        pendingColor = "border",
        trackColor = "surface-subtle",
        height = 12,
        label,
        testID,
    } = props

    const fill = clamp(value)
    const flag = clamp(flagAt ?? value)
    const marker = flagColor ?? color

    return (
        <View testID={testID} style={styles.container}>
            <View style={styles.flagRow}>
                {label != null && <View style={styles.label}>{label}</View>}
                <View style={styles.flag(flag)}>
                    <Icon name="flag" solid size={FLAG_SIZE} color={marker} />
                </View>
            </View>
            <View style={styles.trackWrap}>
                <View style={styles.track(trackColor, height)}>
                    {flag > fill && <View style={styles.bar(pendingColor, flag)} />}
                    <View style={styles.bar(color, fill)} />
                </View>
                <View style={styles.markerLine(marker, flag)} />
            </View>
        </View>
    )
}

const styles = StyleSheet.create((theme) => ({
    container: {
        width: "100%",
    },
    flagRow: {
        minHeight: FLAG_SIZE,
        position: "relative",
        flexDirection: "row",
        alignItems: "flex-end",
    },
    label: {
        alignSelf: "flex-end",
    },
    flag: (pct: number) => ({
        position: "absolute" as const,
        bottom: 0,
        left: `${pct}%` as `${number}%`,
        // The FontAwesome flag glyph's pole sits ~2px from the icon's left edge; offset by that (plus the
        // 1px half-width of the marker line) so the pole lands exactly on the line rather than the box centre.
        marginLeft: -2,
    }),
    trackWrap: {
        position: "relative",
    },
    track: (trackColor: SemanticColorKey, height: number) => ({
        width: "100%" as const,
        height,
        borderRadius: theme.radius.full,
        backgroundColor: theme.colors[trackColor],
        overflow: "hidden" as const,
    }),
    bar: (color: SemanticColorKey, pct: number) => ({
        position: "absolute" as const,
        left: 0,
        top: 0,
        bottom: 0,
        width: `${pct}%` as `${number}%`,
        backgroundColor: theme.colors[color],
    }),
    markerLine: (color: SemanticColorKey, pct: number) => ({
        position: "absolute" as const,
        top: 0,
        bottom: 0,
        left: `${pct}%` as `${number}%`,
        width: 2,
        marginLeft: -1,
        backgroundColor: theme.colors[color],
    }),
}))
